import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { Search, Ban, Check, Loader2, Crown, CircleSlash } from 'lucide-react';

const PAGE_SIZE = 20;

const isPremiumStatus = (u) => {
  const s = String(u?.subscription_status ?? '').toLowerCase();
  return ['premium', 'active', 'trialing'].includes(s);
};

const formatEnd = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: '', blocked: '', verified: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loadError, setLoadError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [subError, setSubError] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const f = {};
    if (filters.role) f.role = filters.role;
    if (filters.blocked === 'yes') f.blocked = true;
    if (filters.blocked === 'no') f.blocked = false;
    if (filters.verified === 'yes') f.verified = true;
    if (filters.verified === 'no') f.verified = false;
    if (debouncedSearch) f.search = debouncedSearch;
    const { data, total: t, error: err } = await adminService.listUsers(page, PAGE_SIZE, f);
    setUsers(data);
    setTotal(t ?? 0);
    if (err) setLoadError(err);
    setLoading(false);
  }, [page, filters.role, filters.blocked, filters.verified, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubscriptionUpdate = async (userId, payload) => {
    setSubError('');
    setUpdatingId(userId);
    try {
      await adminService.setUserSubscription(userId, payload);
      await load();
    } catch (e) {
      setSubError(e.message || 'Could not update subscription');
    }
    setUpdatingId(null);
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">Users</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Search by name, email, or username…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 text-slate-800 text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none"
            aria-label="Search users"
          />
        </div>
        <select
          value={filters.role}
          onChange={(e) => { setFilters((f) => ({ ...f, role: e.target.value })); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
        >
          <option value="">All roles</option>
          <option value="user">User</option>
          <option value="service_provider">Service provider</option>
        </select>
        <select
          value={filters.blocked}
          onChange={(e) => { setFilters((f) => ({ ...f, blocked: e.target.value })); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
        >
          <option value="">All</option>
          <option value="yes">Blocked</option>
          <option value="no">Not blocked</option>
        </select>
        <select
          value={filters.verified}
          onChange={(e) => { setFilters((f) => ({ ...f, verified: e.target.value })); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
        >
          <option value="">All</option>
          <option value="yes">Verified</option>
          <option value="no">Not verified</option>
        </select>
      </div>

      {subError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">{subError}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
        </div>
      ) : loadError ? (
        <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
          <p className="font-medium">Could not load users</p>
          <p className="text-sm mt-1">{loadError}</p>
          <p className="text-sm mt-2">Ensure you ran the admin setup SQL (e.g. docs/run_admin_setup.sql) so admin can read user_profiles.</p>
        </div>
      ) : users.length === 0 ? (
        <div className="p-8 rounded-xl bg-white border border-slate-200 text-center text-slate-500">
          <p>No users found.</p>
          <p className="text-sm mt-1">
            {debouncedSearch ? 'Try a different search term.' : 'Users appear here after they register.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-700">Display name</th>
                  <th className="text-left p-3 font-medium text-slate-700">Email</th>
                  <th className="text-left p-3 font-medium text-slate-700">Role</th>
                  <th className="text-left p-3 font-medium text-slate-700">City</th>
                  <th className="text-left p-3 font-medium text-slate-700">Subscription</th>
                  <th className="text-left p-3 font-medium text-slate-700">Status</th>
                  <th className="text-left p-3 font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const premium = isPremiumStatus(u);
                  const busy = updatingId === u.id;
                  return (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3">
                        <Link to={`/admin/users/${u.id}`} className="text-primary hover:underline font-medium">
                          {u.display_name || u.username || '—'}
                        </Link>
                        {u.is_dummy && <span className="ml-1 text-xs text-amber-600">(dummy)</span>}
                      </td>
                      <td className="p-3 text-slate-600">{u.email}</td>
                      <td className="p-3">{u.role}</td>
                      <td className="p-3">{u.city || '—'}</td>
                      <td className="p-3 align-top">
                        <div className="flex flex-col gap-2 max-w-[280px]">
                          {premium ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                              <Crown className="h-4 w-4 shrink-0" />
                              Premium
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-600">
                              <CircleSlash className="h-4 w-4 shrink-0 text-slate-400" />
                              Free
                            </span>
                          )}
                          {premium && u.subscription_end_at && (
                            <span className="text-xs text-slate-500">
                              Until {formatEnd(u.subscription_end_at)}
                            </span>
                          )}
                          {premium ? (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  handleSubscriptionUpdate(u.id, { action: 'deactivate' })
                                }
                                className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-semibold border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 disabled:opacity-50"
                              >
                                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                Deactivate subscription
                              </button>
                              <p className="text-[11px] text-slate-500 leading-snug">
                                Or replace end date from today:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    handleSubscriptionUpdate(u.id, {
                                      action: 'activate',
                                      durationDays: 30,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                                >
                                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                  Set 1 month
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    handleSubscriptionUpdate(u.id, {
                                      action: 'activate',
                                      durationDays: 365,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                                >
                                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                  Set 1 year
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-[11px] text-slate-500 leading-snug">
                                Grant premium from today:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    handleSubscriptionUpdate(u.id, {
                                      action: 'activate',
                                      durationDays: 30,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                >
                                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                  1 month
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    handleSubscriptionUpdate(u.id, {
                                      action: 'activate',
                                      durationDays: 365,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50"
                                >
                                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                  1 year
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {u.is_blocked ? (
                            <span className="text-red-600 flex items-center gap-1">
                              <Ban className="h-4 w-4" /> Blocked
                            </span>
                          ) : (
                            <span className="text-slate-600">Active</span>
                          )}
                          {u.is_verified && (
                            <span className="text-emerald-600 flex items-center gap-1">
                              <Check className="h-4 w-4" /> Verified
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Link to={`/admin/users/${u.id}`} className="text-primary hover:underline">
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="mt-4 flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 rounded-lg border border-slate-300 text-slate-700 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="py-1 text-slate-600">
                Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="px-3 py-1 rounded-lg border border-slate-300 text-slate-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminUsersPage;
